/**
 * What a member's Brain contains, and which parts of it are worth showing.
 *
 * There are two outputs — the PDF a member exports, and the share link they send a
 * client — and until VA-221 they disagreed. The PDF rendered from the rich
 * `BrandGuideData` object; the share page rendered from a 70-column snapshot table
 * that had no service plans at all. Two hand-maintained shapes, so a section added
 * to one was always missing from the other.
 *
 * Everything about *what appears* lives here, once. The two renderers decide how it
 * looks; neither decides what it is. Same reasoning as `_shared/report-cadence.ts` —
 * when two paths must agree, a comment saying "keep these in step" is not a mechanism.
 */

import type { BrandGuideData } from "@/components/brand-guide/BrandGuideWizard";

/**
 * Text the voice analyser used to STORE when extraction failed.
 *
 * These are not rendered placeholders — they were written into `brand_promise` and
 * `writing_style` in the database, so every reader saw a perfectly non-empty field
 * and printed it. A member sent "Unable to determine - please provide more content
 * samples" to a paying client under their own branding.
 *
 * The analyser no longer writes them and a migration cleared the rows that had them,
 * but a snapshot shared before that fix still carries the text — so they are treated
 * as empty on read, for good. Matched on the opening words rather than the whole
 * sentence, so a reworded variant is still caught.
 */
export const VOICE_FAILURE_SENTINELS = [
  /^unable to determine\b/i,
  /^unable to extract\b/i,
];

/**
 * Is there nothing worth showing here?
 *
 * The load-bearing rule of this file. Empty, whitespace, an empty array, or one of
 * the stored failure sentinels above.
 */
export function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.filter((v) => !isBlank(v)).length === 0;
  if (typeof value === "number") return false;
  if (typeof value === "string") {
    const t = value.trim();
    if (!t) return true;
    return VOICE_FAILURE_SENTINELS.some((re) => re.test(t));
  }
  if (typeof value === "object") return Object.values(value).every(isBlank);
  return false;
}

/** Convenience: the first value that is not blank, or undefined. */
export function firstPresent(...values: unknown[]): string | undefined {
  for (const v of values) if (!isBlank(v) && typeof v === "string") return v.trim();
  return undefined;
}

export type BrainSectionId =
  | "brandCore"
  | "brandStyle"
  | "brandVoice"
  | "callsToAction"
  | "servicePlans";

export interface BrainField {
  label: string;
  /** Already filtered — a field only exists here if it has something in it. */
  value: string | string[];
}

export interface BrainSection {
  id: BrainSectionId;
  title: string;
  fields: BrainField[];
  /** Service plans keep their own shape; the renderers lay them out as cards. */
  plans?: NonNullable<BrandGuideData["servicePlans"]>["plans"];
}

export interface BrainExport {
  brandName?: string;
  /** Style values the renderers use for theming, not for display as text. */
  theme: {
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    logoLightUrl?: string;
    logoDarkUrl?: string;
    headingFont?: string;
    bodyFont?: string;
  };
  sections: BrainSection[];
}

/** Drop the blank ones. A field with nothing in it is not a field. */
function fields(...defs: Array<[string, unknown]>): BrainField[] {
  const out: BrainField[] = [];
  for (const [label, raw] of defs) {
    if (isBlank(raw)) continue;
    if (Array.isArray(raw)) {
      out.push({ label, value: raw.filter((v) => !isBlank(v)).map((v) => String(v).trim()) });
    } else {
      out.push({ label, value: String(raw).trim() });
    }
  }
  return out;
}

/**
 * Assemble the whole Brain from the wizard's data object.
 *
 * Sections carrying no fields are dropped entirely — that is the acceptance criterion
 * "a section with nothing filled in is left out of both. No empty heading, no
 * placeholder sentence, no error."
 */
export function buildBrainExport(data: Partial<BrandGuideData>): BrainExport {
  const sections: BrainSection[] = [];

  const brandCore = fields(
    ["Who you serve", data.avatarName],
    ["Their occupation", data.occupation],
    ["What they want", data.primaryGoal],
    ["Other goals", data.secondaryGoals],
    ["What they desire", data.desires],
    ["Their biggest challenge", data.biggestChallenge],
    ["Daily frustrations", data.dailyFrustrations],
    ["What they fear", data.fears],
    ["Where they spend time", data.onlinePlatforms],
    ["Who they follow", data.influencers],
    ["Before", data.beforeState],
    ["After", data.afterState],
    ["The transformation", data.transformationStory],
    ["How they see you", data.customerPositioning],
    ["Your expertise", data.expertPositioning],
    ["Value propositions", data.valuePropositions],
    ["Identity pillars", data.identityPillars],
    ["Brand personality", data.brandPersonality],
    ["Tagline", data.tagline],
    ["Secondary tagline", data.secondaryTagline],
    ["Elevator pitch", data.elevatorPitch],
    ["Industry", data.industry],
    ["What you offer", data.productsServices],
    ["Social proof", data.socialProof],
    ["Your story", data.brandStoryContent],
    ["Market position", data.marketPosition],
  );
  if (brandCore.length) sections.push({ id: "brandCore", title: "Brand Core", fields: brandCore });

  // Colours are shown by NAME and meaning where the member gave them; the hex values
  // themselves go to the theme, since a client does not need to read "#6a097d".
  const brandStyle = fields(
    ["Primary colour", firstPresent(data.primaryColorName, data.primaryColor)],
    ["What it means", data.primaryColorMeaning],
    ["Secondary colour", firstPresent(data.secondaryColorName, data.secondaryColor)],
    ["What it means ", data.secondaryColorMeaning],
    ["Accent colour", firstPresent(data.accentColorName, data.accentColor)],
    ["Heading font", data.headingFont],
    ["Body font", data.bodyFont],
  );
  if (brandStyle.length) sections.push({ id: "brandStyle", title: "Brand Style", fields: brandStyle });

  const brandVoice = fields(
    ["Tone of voice", data.toneOfVoice],
    ["Writing style", data.writingStyle],
    ["Brand promise", data.brandPromise],
    ["Brand values", data.brandValues],
    ["Phrases you use", data.keyPhrases],
    ["What makes you different", data.competitorDifferentiator],
    ["Your unique selling point", data.uniqueSellingProposition],
  );
  if (brandVoice.length) sections.push({ id: "brandVoice", title: "Brand Voice", fields: brandVoice });

  const ctas = fields(
    ["Primary call to action", data.callToAction],
    ["Who it is for", data.targetAudience],
    ["The problem it solves", data.customerPainPoints],
    ["What happens if they do nothing", data.risksOfInaction],
  );
  if (ctas.length) sections.push({ id: "callsToAction", title: "Calls to Action", fields: ctas });

  const plans = (data.servicePlans?.plans ?? []).filter(
    (p) => p && !isBlank(p.name ?? "") ,
  );
  if (plans.length) {
    sections.push({ id: "servicePlans", title: "Service Plans", fields: [], plans });
  }

  return {
    brandName: firstPresent(data.businessName, data.avatarName),
    theme: {
      primaryColor: firstPresent(data.primaryColor),
      secondaryColor: firstPresent(data.secondaryColor),
      accentColor: firstPresent(data.accentColor),
      logoLightUrl: firstPresent(data.logoLightUrl, data.logoUrl),
      logoDarkUrl: firstPresent(data.logoDarkUrl, data.logoUrl),
      headingFont: firstPresent(data.headingFont),
      bodyFont: firstPresent(data.bodyFont),
    },
    sections,
  };
}

/** The sections with something in them, in display order. */
export function presentSections(model: BrainExport): BrainSection[] {
  return model.sections.filter((s) => s.fields.length > 0 || (s.plans?.length ?? 0) > 0);
}

/**
 * What the Brain is called (VA-222).
 *
 * "<brand> Brain", or just "Brain" when the brand has no name — never a stray
 * placeholder, and never the old "Brand Guide".
 */
export function brainTitle(brandName?: string | null): string {
  const name = (brandName ?? "").trim();
  return name ? `${name} Brain` : "Brain";
}


/**
 * The Brain, in the shape the export template renders, safe to publish.
 *
 * `shared_brand_guides` is readable by anyone — that is how a share link works — so
 * this is an explicit ALLOW-LIST, not a copy of the wizard's state with a few fields
 * deleted. `customBrandPrompt` and `specialInstructions` are the member's private
 * instructions to their assistant and must never reach it; a deny-list would leak
 * the next private field somebody adds to the wizard.
 *
 * Everything here is a field the PDF already renders, so the share link and the PDF
 * are the same template over the same data — which is the point of VA-221.
 */
export function toShareableGuide(data: Partial<BrandGuideData>): Record<string, unknown> {
  const keys: Array<keyof BrandGuideData> = [
    // Customer avatar
    "avatarName", "age", "occupation", "incomeLevel", "education", "location",
    "primaryGoal", "secondaryGoals", "desires",
    "biggestChallenge", "dailyFrustrations", "fears",
    "onlinePlatforms", "influencers", "contentPreferences",
    "beforeState", "afterState", "transformationStory",
    // Positioning, value, identity
    "customerPositioning", "expertPositioning", "valuePropositions",
    "identityPillars", "brandPersonality",
    "tagline", "secondaryTagline", "elevatorPitch",
    // Style
    "primaryColor", "primaryColorName", "primaryColorMeaning",
    "secondaryColor", "secondaryColorName", "secondaryColorMeaning",
    "accentColor", "accentColorName", "accentColorMeaning",
    "tertiaryColor", "tertiaryColorName", "tertiaryColorMeaning",
    "anchorDarkColor", "anchorLightColor",
    "logoUrl", "logoLightUrl", "logoDarkUrl", "headingFont", "bodyFont",
    // Voice
    "toneOfVoice", "brandPromise", "brandValues", "writingStyle", "keyPhrases",
    "whatYouDo", "uniqueSellingProposition", "competitorDifferentiator",
    // Calls to action
    "targetAudience", "customerPainPoints", "risksOfInaction", "callToAction",
    "solutionSteps",
    // Business context
    "businessName", "signatureName",
    "industry", "productsServices", "socialProof", "brandStoryContent", "marketPosition",
    // The section the share link never had
    "servicePlans",
  ];

  const out: Record<string, unknown> = {};
  for (const k of keys) {
    const v = (data as Record<string, unknown>)[k as string];
    if (!isBlank(v)) out[k as string] = v;
  }
  return out;
}
